'use client';

import { useState } from 'react';
import { Globe, Info, Ruler } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@kit/ui/badge';
import { Button } from '@kit/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@kit/ui/dialog';
import { Label } from '@kit/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@kit/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@kit/ui/table';
import { Trans } from '@kit/ui/trans';
import { cn } from '@kit/ui/utils';

type SizeType = 'clothing' | 'shoe';
type Gender = 'male' | 'female' | 'unisex';
type Region = 'EU' | 'US' | 'UK';

interface SizeSelectorProps {
  type: SizeType;
  value?: string;
  onChange: (size: string) => void;
  gender?: Gender;
  region?: Region;
  showSizeGuide?: boolean;
  error?: string;
  required?: boolean;
  className?: string;
  label?: string;
}

// Clothing sizes
const CLOTHING_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];

// Shoe sizes with conversions
const SHOE_SIZES = {
  EU: {
    female: [
      '35',
      '35.5',
      '36',
      '36.5',
      '37',
      '37.5',
      '38',
      '38.5',
      '39',
      '39.5',
      '40',
      '40.5',
      '41',
      '41.5',
      '42',
      '42.5',
      '43',
      '43.5',
      '44',
      '44.5',
      '45',
    ],
    male: [
      '38',
      '38.5',
      '39',
      '39.5',
      '40',
      '40.5',
      '41',
      '41.5',
      '42',
      '42.5',
      '43',
      '43.5',
      '44',
      '44.5',
      '45',
      '45.5',
      '46',
    ],
    unisex: [
      '35',
      '36',
      '37',
      '38',
      '39',
      '40',
      '41',
      '42',
      '43',
      '44',
      '45',
      '46',
    ],
  },
  US: {
    female: [
      '5',
      '5.5',
      '6',
      '6.5',
      '7',
      '7.5',
      '8',
      '8.5',
      '9',
      '9.5',
      '10',
      '10.5',
      '11',
      '11.5',
      '12',
      '12.5',
      '13',
      '13.5',
      '14',
      '14.5',
      '15',
    ],
    male: [
      '6',
      '6.5',
      '7',
      '7.5',
      '8',
      '8.5',
      '9',
      '9.5',
      '10',
      '10.5',
      '11',
      '11.5',
      '12',
      '12.5',
      '13',
      '13.5',
      '14',
    ],
    unisex: ['5', '6', '7', '8', '9', '10', '11', '12', '13', '14'],
  },
  UK: {
    female: [
      '2.5',
      '3',
      '3.5',
      '4',
      '4.5',
      '5',
      '5.5',
      '6',
      '6.5',
      '7',
      '7.5',
      '8',
      '8.5',
      '9',
      '9.5',
      '10',
      '10.5',
      '11',
      '11.5',
      '12',
      '12.5',
    ],
    male: [
      '5.5',
      '6',
      '6.5',
      '7',
      '7.5',
      '8',
      '8.5',
      '9',
      '9.5',
      '10',
      '10.5',
      '11',
      '11.5',
      '12',
      '12.5',
      '13',
      '13.5',
    ],
    unisex: ['3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13'],
  },
};

// Clothing size conversion chart
const CLOTHING_CONVERSION = {
  XS: {
    EU: '32-34',
    US: '0-2',
    UK: '4-6',
    measurements: 'Bust: 78-82cm, Waist: 58-62cm',
  },
  S: {
    EU: '36-38',
    US: '4-6',
    UK: '8-10',
    measurements: 'Bust: 82-86cm, Waist: 62-66cm',
  },
  M: {
    EU: '40-42',
    US: '8-10',
    UK: '12-14',
    measurements: 'Bust: 86-90cm, Waist: 66-70cm',
  },
  L: {
    EU: '44-46',
    US: '12-14',
    UK: '16-18',
    measurements: 'Bust: 90-94cm, Waist: 70-74cm',
  },
  XL: {
    EU: '48-50',
    US: '16-18',
    UK: '20-22',
    measurements: 'Bust: 94-98cm, Waist: 74-78cm',
  },
  XXL: {
    EU: '52-54',
    US: '20-22',
    UK: '24-26',
    measurements: 'Bust: 98-102cm, Waist: 78-82cm',
  },
};

// Shoe size conversion (approximate)
const _SHOE_CONVERSION = {
  // EU to US (female)
  '35': { US_F: '5', UK_F: '2.5' },
  '36': { US_F: '6', UK_F: '3.5' },
  '37': { US_F: '6.5', UK_F: '4' },
  '38': { US_F: '7.5', UK_F: '5' },
  '39': { US_F: '8.5', UK_F: '6' },
  '40': { US_F: '9', UK_F: '6.5' },
  '41': { US_F: '9.5', UK_F: '7' },
  '42': { US_F: '10.5', UK_F: '8' },
  // Add more conversions as needed
};

const SizeGuideContent = ({
  type,
  gender,
}: {
  type: SizeType;
  gender: Gender;
}) => {
  if (type === 'clothing') {
    return (
      <div className="space-y-4">
        <div>
          <h4 className="mb-2 font-medium">
            <Trans i18nKey="dancer:sizeSelector.chart.sizeChart" />
          </h4>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <Trans i18nKey="dancer:sizeSelector.chart.size" />
                </TableHead>
                <TableHead>
                  <Trans i18nKey="dancer:sizeSelector.chart.eu" />
                </TableHead>
                <TableHead>
                  <Trans i18nKey="dancer:sizeSelector.chart.us" />
                </TableHead>
                <TableHead>
                  <Trans i18nKey="dancer:sizeSelector.chart.uk" />
                </TableHead>
                <TableHead>
                  <Trans i18nKey="dancer:sizeSelector.chart.measurements" />
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Object.entries(CLOTHING_CONVERSION).map(
                ([size, conversions]) => (
                  <TableRow key={size}>
                    <TableCell className="font-medium">{size}</TableCell>
                    <TableCell>{conversions.EU}</TableCell>
                    <TableCell>{conversions.US}</TableCell>
                    <TableCell>{conversions.UK}</TableCell>
                    <TableCell className="text-sm">
                      {conversions.measurements}
                    </TableCell>
                  </TableRow>
                ),
              )}
            </TableBody>
          </Table>
        </div>

        <div className="text-muted-foreground text-sm">
          <p>
            <strong>
              <Trans i18nKey="dancer:sizeSelector.chart.measuringTips" />
            </strong>
          </p>
          <ul className="mt-1 list-inside list-disc space-y-1">
            <li>
              <Trans i18nKey="dancer:sizeSelector.chart.tipBust" />
            </li>
            <li>
              <Trans i18nKey="dancer:sizeSelector.chart.tipWaist" />
            </li>
            <li>
              <Trans i18nKey="dancer:sizeSelector.chart.tipHips" />
            </li>
            <li>
              <Trans i18nKey="dancer:sizeSelector.chart.tipTape" />
            </li>
            <li>
              <Trans i18nKey="dancer:sizeSelector.chart.tipUndergarments" />
            </li>
          </ul>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h4 className="mb-2 font-medium">
          <Trans i18nKey="dancer:sizeSelector.chart.shoeConversion" />
        </h4>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <h5 className="mb-2 text-sm font-medium">
              <Trans i18nKey="dancer:sizeSelector.chart.euSizes" />
            </h5>
            <div className="space-y-1 text-sm">
              {SHOE_SIZES.EU[gender === 'male' ? 'male' : 'female']
                .slice(0, 8)
                .map((size) => (
                  <div key={size} className="flex justify-between">
                    <span>EU {size}</span>
                  </div>
                ))}
            </div>
          </div>
          <div>
            <h5 className="mb-2 text-sm font-medium">
              <Trans i18nKey="dancer:sizeSelector.chart.usSizes" />
            </h5>
            <div className="space-y-1 text-sm">
              {SHOE_SIZES.US[gender === 'male' ? 'male' : 'female']
                .slice(0, 8)
                .map((size) => (
                  <div key={size} className="flex justify-between">
                    <span>US {size}</span>
                  </div>
                ))}
            </div>
          </div>
          <div>
            <h5 className="mb-2 text-sm font-medium">
              <Trans i18nKey="dancer:sizeSelector.chart.ukSizes" />
            </h5>
            <div className="space-y-1 text-sm">
              {SHOE_SIZES.UK[gender === 'male' ? 'male' : 'female']
                .slice(0, 8)
                .map((size) => (
                  <div key={size} className="flex justify-between">
                    <span>UK {size}</span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>

      <div className="text-muted-foreground text-sm">
        <p>
          <strong>
            <Trans i18nKey="dancer:sizeSelector.chart.measuringTips" />
          </strong>
        </p>
        <ul className="mt-1 list-inside list-disc space-y-1">
          <li>
            <Trans i18nKey="dancer:sizeSelector.chart.tipAfternoon" />
          </li>
          <li>
            <Trans i18nKey="dancer:sizeSelector.chart.tipLargerFoot" />
          </li>
          <li>
            <Trans i18nKey="dancer:sizeSelector.chart.tipShoeType" />
          </li>
          <li>
            <Trans i18nKey="dancer:sizeSelector.chart.tipToePadding" />
          </li>
        </ul>
      </div>
    </div>
  );
};

export function SizeSelector({
  type,
  value,
  onChange,
  gender = 'female',
  region = 'EU',
  showSizeGuide = true,
  error,
  required = false,
  className,
  label,
}: SizeSelectorProps) {
  const [selectedRegion, setSelectedRegion] = useState<Region>(region);
  const { t } = useTranslation();

  const defaultLabel =
    type === 'clothing'
      ? t('dancer:sizeSelector.clothingSize')
      : t('dancer:sizeSelector.shoeSize');
  const displayLabel = label || defaultLabel;

  if (type === 'clothing') {
    return (
      <div className={className}>
        <Label className="flex items-center gap-2">
          <Ruler className="text-muted-foreground h-4 w-4" />
          {displayLabel}
          {required && <span className="text-destructive">*</span>}
        </Label>

        <div>
          <div className="space-y-3">
            {/* Size Selection */}
            <div className="grid grid-cols-3 gap-2">
              {CLOTHING_SIZES.map((size) => (
                <Button
                  key={size}
                  type="button"
                  variant={value === size ? 'default' : 'outline'}
                  className={cn(
                    'h-12 text-sm font-medium',
                    value === size && 'ring-ring ring-2 ring-offset-2',
                  )}
                  onClick={() => onChange(size)}
                >
                  {size}
                </Button>
              ))}
            </div>

            {/* Selected Size Info */}
            {value &&
              CLOTHING_CONVERSION[
                value as keyof typeof CLOTHING_CONVERSION
              ] && (
                <div className="flex gap-2">
                  <Badge variant="secondary" className="text-xs">
                    EU:{' '}
                    {
                      CLOTHING_CONVERSION[
                        value as keyof typeof CLOTHING_CONVERSION
                      ].EU
                    }
                  </Badge>
                  <Badge variant="secondary" className="text-xs">
                    US:{' '}
                    {
                      CLOTHING_CONVERSION[
                        value as keyof typeof CLOTHING_CONVERSION
                      ].US
                    }
                  </Badge>
                  <Badge variant="secondary" className="text-xs">
                    UK:{' '}
                    {
                      CLOTHING_CONVERSION[
                        value as keyof typeof CLOTHING_CONVERSION
                      ].UK
                    }
                  </Badge>
                </div>
              )}

            {/* Size Guide */}
            {showSizeGuide && (
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-xs">
                    <Info className="mr-1 h-3 w-3" />
                    <Trans i18nKey="dancer:sizeSelector.sizeGuide" />
                  </Button>
                </DialogTrigger>
                <DialogContent className="flex max-h-[90vh] flex-col overflow-hidden sm:max-w-4xl">
                  <DialogHeader className="flex-shrink-0">
                    <DialogTitle>
                      <Trans i18nKey="dancer:sizeSelector.clothingSizeGuide" />
                    </DialogTitle>
                    <DialogDescription>
                      <Trans i18nKey="dancer:sizeSelector.clothingSizeGuideDescription" />
                    </DialogDescription>
                  </DialogHeader>
                  <div className="flex-1 overflow-y-auto pr-2">
                    <SizeGuideContent type="clothing" gender={gender} />
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {error && <p className="text-xs text-red-500">{error}</p>}
      </div>
    );
  }

  // Shoe size selector
  const availableSizes =
    SHOE_SIZES[selectedRegion][gender] || SHOE_SIZES[selectedRegion].unisex;

  return (
    <div className={className}>
      <Label className="flex items-center gap-2">
        <Ruler className="text-muted-foreground h-4 w-4" />
        {displayLabel}
        {required && <span className="text-destructive">*</span>}
      </Label>

      <div>
        <div className="space-y-3">
          {/* Region Selector */}
          <div className="flex items-center gap-2">
            <Globe className="text-muted-foreground h-4 w-4" />
            <Select
              value={selectedRegion}
              onValueChange={(value: Region) => setSelectedRegion(value)}
            >
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="EU">
                  <Trans i18nKey="dancer:sizeSelector.chart.eu" />
                </SelectItem>
                <SelectItem value="US">
                  <Trans i18nKey="dancer:sizeSelector.chart.us" />
                </SelectItem>
                <SelectItem value="UK">
                  <Trans i18nKey="dancer:sizeSelector.chart.uk" />
                </SelectItem>
              </SelectContent>
            </Select>
            <span className="text-muted-foreground text-sm">
              <Trans i18nKey="dancer:sizeSelector.sizing" />
            </span>
          </div>

          {/* Size Selection Grid */}
          <div className="grid grid-cols-5 gap-2">
            {availableSizes.map((size) => (
              <Button
                key={size}
                type="button"
                variant={value === size ? 'default' : 'outline'}
                className={cn(
                  'h-10 text-sm',
                  value === size && 'ring-ring ring-2 ring-offset-2',
                )}
                onClick={() => onChange(size)}
              >
                {size}
              </Button>
            ))}
          </div>

          {/* Size Guide */}
          {showSizeGuide && (
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="ghost" size="sm" className="text-xs">
                  <Info className="mr-1 h-3 w-3" />
                  <Trans i18nKey="dancer:sizeSelector.sizeGuideConversions" />
                </Button>
              </DialogTrigger>
              <DialogContent className="flex max-h-[90vh] flex-col overflow-hidden sm:max-w-2xl">
                <DialogHeader className="flex-shrink-0">
                  <DialogTitle>
                    <Trans i18nKey="dancer:sizeSelector.shoeSizeGuide" />
                  </DialogTitle>
                  <DialogDescription>
                    <Trans i18nKey="dancer:sizeSelector.shoeSizeGuideDescription" />
                  </DialogDescription>
                </DialogHeader>
                <div className="flex-1 overflow-y-auto pr-2">
                  <SizeGuideContent type="shoe" gender={gender} />
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}

export { CLOTHING_SIZES, SHOE_SIZES, CLOTHING_CONVERSION };
export type { SizeType, Gender, Region };
